import { RESOLVED_COMMENT_COLOR } from 'constants/annotation'
import { isCommentAnnotation } from 'domain/comment'
import { CommentDomainService } from 'domain/comment/services/CommentDomainService'
import { Annotation, AnnotationType } from 'types/building'
import { KrpanoInterface } from 'types/krpano'
import { mapAnnotationTypeToCategory } from 'utils/annotationRN'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

export interface AddRNAnnotationOptions {
  krpano: KrpanoInterface
  annotation: Annotation
  h?: number
  v?: number
  annotationType?: AnnotationType | number | string
  isDisplay?: string | boolean | null
  isShowEmptyMark?: boolean
  fetchMemoContent?: (s3Link: string) => Promise<string>
  userList?: any[]
  currentUser?: { id?: number | string; role?: string } | null
}

export interface AddRNCommentOptions {
  krpano: KrpanoInterface
  id?: string | number
  h?: number
  v?: number
  annotation: Annotation
  isDisplay?: string | boolean | null
  userList?: any[]
  currentUser?: { id?: number | string; role?: string } | null
}

export interface AddRNAnnotationHotspotOptions {
  krpano: KrpanoInterface
  id: string | number
  h?: number
  v?: number
  annotation: Annotation
  cat: string
  isDisplay?: string | boolean | null
  year?: string
  displayText?: string
  isShowEmptyMark?: boolean
  isDataEmpty?: boolean
}

export class KrpanoRN {
  static categories: string[] = ['G', 'A', 'S', 'M', 'E']

  static features: string[] = ['annotation', 'comment']

  /**
   * Injects CSS styles for Renewal markers into document head once
   */
  static EnsureRNMarkerStyles(): void {
    if (typeof document === 'undefined') return

    const styleId = 'krpano-rn-marker-styles'
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      .rn-marker-container {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .rn-comment-marker-open {
        width: 40px;
        height: 40px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 8px rgba(0,0,0,0.25);
        border: 2px solid white;
        transition: transform 0.2s ease;
      }
      .rn-annotation-marker-circle {
        width: 44px;
        height: 44px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 8px rgba(0,0,0,0.25);
        border: 2px solid white;
        transition: transform 0.2s ease;
      }
      .rn-annotation-marker-circle:hover {
        transform: scale(1.1);
      }
      .rn-marker-container .rn-annotation-marker-circle span.rn-marker-letter,
      .rn-marker-container .rn-marker-letter,
      span.rn-marker-letter {
        font-family: Georgia, 'Times New Roman', serif !important;
        font-size: 22px !important;
        font-weight: 900 !important;
        line-height: 1 !important;
        color: #FFFFFF !important;
        user-select: none;
        display: block;
      }
      .rn-comment-marker-resolved {
        width: 20px;
        height: 20px;
        border-radius: 9999px;
        background-color: #16A34A;
        opacity: 0.75;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        border: 1.5px solid white;
        transition: transform 0.2s ease, opacity 0.2s ease;
      }
      .rn-comment-marker-resolved:hover {
        transform: scale(1.25);
        opacity: 1;
      }
      .rn-badge {
        position: absolute;
        width: 14px;
        height: 14px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8px;
        font-weight: 900;
        color: white;
        border: 1.5px solid white;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        line-height: 1;
      }
      .rn-badge-status-open {
        bottom: -2px;
        right: -2px;
        background-color: #F43F5E;
      }
      .rn-badge-lock {
        top: -2px;
        left: -2px;
        background-color: #D97706;
      }
      .rn-badge-set {
        position: absolute;
        top: -3px;
        left: -3px;
        width: 18px;
        height: 18px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 5;
        border: 1.5px solid white;
        box-shadow: 0 1px 3px rgba(0,0,0,0.35);
        color: white;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 10px;
        font-weight: 900;
        line-height: 1;
        box-sizing: border-box;
      }
      .rn-badge-empty {
        position: absolute;
        top: -3px;
        right: -3px;
        width: 18px;
        height: 18px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 5;
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.35));
      }
      .rn-badge-empty img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
      }
      .rn-badge-year {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: #FFFFFF;
        padding: 1px 5px;
        border-radius: 9999px;
        font-size: 9.5px;
        font-weight: 800;
        box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        white-space: nowrap;
        line-height: 1.1;
        border: 1px solid rgba(0,0,0,0.12);
      }
      .rn-marker-stem {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 2px;
        height: 24px;
        pointer-events: none;
      }
      .rn-marker-dot {
        position: absolute;
        top: calc(100% + 24px);
        left: 50%;
        transform: translateX(-50%);
        width: 8px;
        height: 8px;
        border-radius: 9999px;
        background-color: #FFFFFF;
        box-shadow: 0 1px 4px rgba(0,0,0,0.3), 0 0 0 2px rgba(255,255,255,0.4);
        pointer-events: none;
      }
      .rn-marker-tooltip {
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        background-color: rgba(15, 23, 42, 0.9);
        color: #FFFFFF;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        width: max-content;
        max-width: 250px;
        white-space: normal;
        word-break: break-word;
        text-align: center;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 10;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
      }
      .rn-marker-container:hover .rn-marker-tooltip {
        opacity: 1;
      }
      .kr-anno-inner {
        pointer-events: auto !important;
        transform: scale(var(--kr-scale, 1));
        transform-origin: bottom center;
      }
    `
    document.head.appendChild(style)
  }

  /**
   * Converts any CSS color (#hex, 0x..., rgb, rgba) to Krpano 0xRRGGBB format
   */
  static toKrpanoHexColor = (color?: string): string => {
    if (!color) return '0xFFFFFF'
    const trimmed = color.trim()
    if (trimmed.startsWith('#')) {
      return trimmed.replace('#', '0x')
    }
    if (trimmed.startsWith('0x')) {
      return trimmed
    }
    const rgbMatch = trimmed.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
    if (rgbMatch) {
      const r = Number(rgbMatch[1]).toString(16).padStart(2, '0')
      const g = Number(rgbMatch[2]).toString(16).padStart(2, '0')
      const b = Number(rgbMatch[3]).toString(16).padStart(2, '0')
      return `0x${r}${g}${b}`.toUpperCase()
    }
    return '0xFFFFFF'
  }

  /**
   * Returns category color for Renewal (RN) series markers
   */
  static GetCategoryColor = (cat?: string): string =>
    CommentDomainService.getCategoryColor(cat)

  /**
   * Main entry for creating Renewal series markers in Krpano
   */
  static async AddRNAnnotation(options: AddRNAnnotationOptions): Promise<void> {
    const {
      krpano,
      annotation,
      h,
      v,
      annotationType,
      isDisplay,
      isShowEmptyMark = true,
      fetchMemoContent,
      userList,
      currentUser
    } = options

    KrpanoRN.EnsureRNMarkerStyles()
    const { id } = annotation
    if (!id) return

    krpano.call(`remove_annotation(${id})`)
    krpano.call(`remove_callout_memo_annotation(${id})`)
    krpano.call(`removehotspot(id-${id})`)
    krpano.call(`removehotspot(empty-${id})`)
    krpano.call(`removehotspot(year-${id})`)
    krpano.call(`removehotspot(tooltip-${id})`)
    krpano.call(`removehotspot(text-${id})`)

    const isComment = isCommentAnnotation(annotation, annotationType)

    if (isComment) {
      const canView = CommentDomainService.canUserViewComment(
        annotation,
        currentUser,
        userList
      )

      if (!canView) {
        krpano.call(`
          remove_annotation(${id});
          remove_callout_memo_annotation(${id});
          removehotspot(id-${id});
          removehotspot(empty-${id});
          removehotspot(year-${id});
          removehotspot(head-${id});
          removehotspot(middle-${id});
          removehotspot(polyline-${id});
          removehotspot(polyline2-${id});
          removehotspot(tooltip-${id});
          removehotspot(text-${id});
        `)
        return
      }

      KrpanoRN.AddRNCommentHotspot({
        krpano,
        id,
        h,
        v,
        annotation,
        isDisplay,
        userList,
        currentUser
      })
    } else {
      // Standard Renewal Annotation or Memo
      const rawCat =
        (annotation as any).object_cat || (annotation as any).objectCat || ''
      const rawYear = annotation.year || (annotation as any).anno_year || ''

      const numType = Number(annotationType) || Number(annotation.type)
      let numericType = numType
      if (
        Number.isNaN(numericType) ||
        !Object.values(AnnotationType).includes(numericType)
      ) {
        numericType = AnnotationType.TRADITION
      }

      const cat = mapAnnotationTypeToCategory(numericType, rawCat)

      if (numericType === Number(AnnotationType.MEMO)) {
        // Exactly as current Shimizu memo - pure Shimizu logic without category, year badge, or category tooltips
        const displayText =
          annotation.set_annotation_name || annotation.name || ''
        const memo = annotation.memo || ''
        const fetchMemo = fetchMemoContent || Krpano.FetchMemoContent
        const textContent = memo ? await fetchMemo(memo) : ''

        try {
          const imgSrcs = Array.from(
            textContent.matchAll(/<img[^>]+src=["']([^"']+)["']/g)
          ).map((m) => m[1])
          await Promise.all(
            imgSrcs.map(
              (src) =>
                new Promise<void>((resolve) => {
                  const img = new Image()
                  img.onload = () => resolve()
                  img.onerror = () => resolve()
                  img.src = src
                })
            )
          )
        } catch {
          // ignore
        }

        const qlTextcontent = `<div class="ql-editor tour-memo p-0">${textContent}</div>`
        const safeHtml = qlTextcontent.replace(/"/g, "'").replace(/,/g, '&#44;')

        const hHead = annotation.head_h || h
        const vHead = annotation.head_v || v
        const hMid = annotation.mid_h || h
        const vMid = annotation.mid_v || v
        const memoHtml = memo ? safeHtml : "''"
        const displayVal = isDisplay || 'true'

        const isViewer =
          !isShowEmptyMark ||
          (typeof window !== 'undefined' &&
            window.location.pathname.includes('/viewer/'))
        const memoSetId = isViewer ? '' : annotation.set_annotation_id ?? ''
        const memoDisplayText = isViewer ? '' : displayText

        krpano.call(
          `add_annotation(${id},${hHead},${vHead},${hMid},${vMid},${h},${v},${numericType},${memoHtml},${memoSetId},${displayVal},'${memoDisplayText}');` +
            `set(hotspot['id-${id}'].anno_kind, 'memo');` +
            `set(hotspot['head-${id}'].anno_kind, 'memo');` +
            `set(hotspot['middle-${id}'].anno_kind, 'memo');` +
            `set(hotspot['polyline-${id}'].anno_kind, 'memo');` +
            `set(hotspot['polyline2-${id}'].anno_kind, 'memo');`
        )
      } else if (
        numericType === Number(AnnotationType.IMAGE) ||
        numericType === Number(AnnotationType.MOVIE) ||
        numericType === Number(AnnotationType.INFO)
      ) {
        krpano.call(
          `add_new_tour_annotation(${id},${h},${v},${numericType}, 'true');` +
            `set(hotspot['id-${id}'].anno_kind, 'annotation');` +
            `set(hotspot['id-${id}'].object_cat, '${cat}');`
        )
      } else {
        const isDataEmpty = Krpano.IsAnnotationDataEmpty(annotation)

        if (isDataEmpty && !isShowEmptyMark) {
          krpano.call(`removehotspot('empty-${id}')`)
          return
        }

        const displayText =
          annotation.set_annotation_name || annotation.name || ''

        KrpanoRN.AddRNAnnotationHotspot({
          krpano,
          id,
          h,
          v,
          annotation,
          cat,
          isDisplay,
          year: rawYear ? String(rawYear).trim() : '',
          displayText,
          isShowEmptyMark,
          isDataEmpty
        })
      }
    }

    KrpanoRN.ApplyMarkerFilter()
  }

  /**
   * Renders Renewal Annotation Hotspot with rounded centered letter (G/A/S/M/E)
   */
  static AddRNAnnotationHotspot = (options: AddRNAnnotationHotspotOptions) => {
    const {
      krpano,
      id,
      h,
      v,
      annotation,
      cat,
      isDisplay,
      year,
      displayText = '',
      isShowEmptyMark = true,
      isDataEmpty = false
    } = options

    KrpanoRN.EnsureRNMarkerStyles()

    krpano.call(`
      remove_annotation(${id});
      removehotspot(id-${id});
      removehotspot(year-${id});
      removehotspot(empty-${id});
      removehotspot(head-${id});
      removehotspot(middle-${id});
      removehotspot(polyline-${id});
      removehotspot(polyline2-${id});
      removehotspot(tooltip-${id});
      removehotspot(text-${id});
    `)

    if (isDataEmpty && !isShowEmptyMark) {
      return
    }

    const catColor = KrpanoRN.GetCategoryColor(cat)

    const hasBranches =
      annotation.head_h !== undefined &&
      annotation.head_h !== null &&
      annotation.mid_h !== undefined &&
      annotation.mid_h !== null

    const hHead = hasBranches ? annotation.head_h : h ?? 0
    const vHead = hasBranches ? annotation.head_v : v ?? 0
    const hMid = hasBranches ? annotation.mid_h : h ?? 0
    const vMid = hasBranches ? annotation.mid_v : v ?? 0
    const hTail = h ?? annotation.h ?? 0
    const vTail = v ?? annotation.v ?? 0

    const stemDotHtml = hasBranches
      ? ''
      : `
      <svg class="rn-marker-stem" viewBox="0 0 2 24">
        <line x1="1" y1="0" x2="1" y2="24" stroke="${catColor}" stroke-width="1.8" stroke-opacity="0.85"></line>
      </svg>
      <span class="rn-marker-dot"></span>
    `

    const isViewer =
      !isShowEmptyMark ||
      (typeof window !== 'undefined' &&
        window.location.pathname.includes('/viewer/'))

    const markerSize = 44

    let setBadgeHtml = ''
    if (!isViewer && annotation.set_annotation_id) {
      setBadgeHtml = `<div class="rn-badge-set" style="background-color: ${catColor};">S</div>`
    }

    let emptyBadgeHtml = ''
    if (isDataEmpty && isShowEmptyMark) {
      emptyBadgeHtml = `<div class="rn-badge-empty"><img src="/krpano/assets/empty_mark.svg" alt="" /></div>`
    }

    const markerInnerHtml = `
      <div class="kr-anno-inner" style="position: relative; display: inline-flex; flex-direction: column; align-items: center; width: ${markerSize}px; height: ${markerSize}px;">
        <div class="rn-marker-container" style="width: ${markerSize}px; height: ${markerSize}px;">
          <div class="rn-annotation-marker-circle" style="background-color: ${catColor}; width: ${markerSize}px; height: ${markerSize}px; border-radius: 9999px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 8px rgba(0,0,0,0.25); border: 2.5px solid white; box-sizing: border-box;">
            <span class="rn-marker-letter">${cat}</span>
          </div>
          ${setBadgeHtml}
          ${emptyBadgeHtml}
        </div>
        ${stemDotHtml}
      </div>
    `

    const safeMarkerHtml = markerInnerHtml
      .replace(/"/g, "'")
      .replace(/,/g, '&#44;')

    let branchHotspotsXml = ''
    let tailDragMode = 'new_tour_tail'

    if (hasBranches) {
      tailDragMode = 'tail'
      const hexColor = KrpanoRN.toKrpanoHexColor(catColor)

      const headHtml =
        `<div style="width:16px;height:16px;border-radius:50%;background-color:${catColor};border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.35);cursor:pointer;box-sizing:border-box;"></div>`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      const middleHtml =
        `<div style="width:10px;height:10px;border-radius:50%;background-color:${catColor};border:1.5px solid #ffffff;box-shadow:0 1px 2px rgba(0,0,0,0.35);cursor:pointer;box-sizing:border-box;"></div>`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      branchHotspotsXml = `
        addhotspot('head-${id}');
        set(hotspot['head-${id}'].type, 'text');
        set(hotspot['head-${id}'].ath, ${hHead});
        set(hotspot['head-${id}'].atv, ${vHead});
        set(hotspot['head-${id}'].edge, 'center');
        set(hotspot['head-${id}'].bg, false);
        set(hotspot['head-${id}'].css, '');
        set(hotspot['head-${id}'].padding, 0);
        set(hotspot['head-${id}'].bgborder, '0');
        set(hotspot['head-${id}'].html, "${headHtml}");
        set(hotspot['head-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['head-${id}'].zorder, 32);
        set(hotspot['head-${id}'].anno_kind, 'annotation');
        set(hotspot['head-${id}'].object_cat, '${cat}');
        set(hotspot['head-${id}'].ondown, drag_hotspot('${id}', 'head'););
        set(hotspot['head-${id}'].onup, if(islocked, drop_hotspot();js(handle_drag_end('${id}', 'head'));););

        addhotspot('middle-${id}');
        set(hotspot['middle-${id}'].type, 'text');
        set(hotspot['middle-${id}'].ath, ${hMid});
        set(hotspot['middle-${id}'].atv, ${vMid});
        set(hotspot['middle-${id}'].edge, 'center');
        set(hotspot['middle-${id}'].bg, false);
        set(hotspot['middle-${id}'].css, '');
        set(hotspot['middle-${id}'].padding, 0);
        set(hotspot['middle-${id}'].bgborder, '0');
        set(hotspot['middle-${id}'].html, "${middleHtml}");
        set(hotspot['middle-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['middle-${id}'].zorder, 32);
        set(hotspot['middle-${id}'].anno_kind, 'annotation');
        set(hotspot['middle-${id}'].object_cat, '${cat}');
        set(hotspot['middle-${id}'].ondown, drag_hotspot('${id}', 'middle'););
        set(hotspot['middle-${id}'].onup, if(islocked, drop_hotspot();js(handle_drag_end('${id}', 'middle'));););

        addhotspot('polyline-${id}');
        hotspot['polyline-${id}'].loadstyle('polyline_style_memo');
        set(hotspot['polyline-${id}'].polyline, true);
        set(hotspot['polyline-${id}'].point[0].ath, ${hHead});
        set(hotspot['polyline-${id}'].point[0].atv, ${vHead});
        set(hotspot['polyline-${id}'].point[1].ath, ${hMid});
        set(hotspot['polyline-${id}'].point[1].atv, ${vMid});
        set(hotspot['polyline-${id}'].bordercolor, ${hexColor});
        set(hotspot['polyline-${id}'].borderwidth, 2);
        set(hotspot['polyline-${id}'].borderalpha, 1);
        set(hotspot['polyline-${id}'].scale, 1);
        set(hotspot['polyline-${id}'].zoom, true);
        set(hotspot['polyline-${id}'].capture, false);
        set(hotspot['polyline-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['polyline-${id}'].zorder, 28);
        set(hotspot['polyline-${id}'].anno_kind, 'annotation');
        set(hotspot['polyline-${id}'].object_cat, '${cat}');

        addhotspot('polyline2-${id}');
        hotspot['polyline2-${id}'].loadstyle('polyline_style_memo');
        set(hotspot['polyline2-${id}'].polyline, true);
        set(hotspot['polyline2-${id}'].point[0].ath, ${hMid});
        set(hotspot['polyline2-${id}'].point[0].atv, ${vMid});
        set(hotspot['polyline2-${id}'].point[1].ath, ${hTail});
        set(hotspot['polyline2-${id}'].point[1].atv, ${vTail});
        set(hotspot['polyline2-${id}'].bordercolor, ${hexColor});
        set(hotspot['polyline2-${id}'].borderwidth, 2);
        set(hotspot['polyline2-${id}'].borderalpha, 1);
        set(hotspot['polyline2-${id}'].scale, 1);
        set(hotspot['polyline2-${id}'].zoom, true);
        set(hotspot['polyline2-${id}'].capture, false);
        set(hotspot['polyline2-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['polyline2-${id}'].zorder, 28);
        set(hotspot['polyline2-${id}'].anno_kind, 'annotation');
        set(hotspot['polyline2-${id}'].object_cat, '${cat}');
      `
    }

    krpano.call(`
      ${branchHotspotsXml}
      addhotspot('id-${id}');
      set(hotspot['id-${id}'].type, 'text');
      set(hotspot['id-${id}'].ath, ${hTail});
      set(hotspot['id-${id}'].atv, ${vTail});
      set(hotspot['id-${id}'].width, ${markerSize});
      set(hotspot['id-${id}'].height, ${markerSize});
      set(hotspot['id-${id}'].edge, 'center');
      set(hotspot['id-${id}'].align, 'center');
      set(hotspot['id-${id}'].bg, false);
      set(hotspot['id-${id}'].css, '');
      set(hotspot['id-${id}'].padding, 0);
      set(hotspot['id-${id}'].bgborder, '0');
      set(hotspot['id-${id}'].anno_kind, 'annotation');
      set(hotspot['id-${id}'].object_cat, '${cat}');
      set(hotspot['id-${id}'].visible, ${isDisplay || 'true'});
      set(hotspot['id-${id}'].zorder, 34);
      set(hotspot['id-${id}'].html, "${safeMarkerHtml}");
      set(hotspot['id-${id}'].onclick, js(handle_click_annotation('${id}')););
      set(hotspot['id-${id}'].ondown, drag_hotspot('${id}', '${tailDragMode}'););
      set(hotspot['id-${id}'].onup, if(islocked, drop_hotspot();js(handle_drag_end('${id}'));););
    `)

    krpano.call(`
      removehotspot('empty-${id}');
      removehotspot('tooltip-${id}');
    `)

    if (!isViewer && annotation.set_annotation_id && displayText) {
      const safeText = displayText.replace(/"/g, "'").replace(/,/g, '&#44;')

      const textCss =
        `text-align:center; font-weight:700; color:${catColor}; margin-top:-40px; font-family:'Inter', 'Noto Sans JP', 'Noto Sans', sans-serif; font-size:11px; line-height:1.21; letter-spacing:0; text-shadow:1px 0 #fff, -1px 0 #fff, 0 1px #fff, 0 -1px #fff, 1px 1px #fff, -1px -1px #fff, 1px -1px #fff, -1px 1px #fff;`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      krpano.call(`
        addhotspot('text-${id}');
        set(hotspot['text-${id}'].type, 'text');
        set(hotspot['text-${id}'].ath, ${hTail});
        set(hotspot['text-${id}'].atv, calc(${vTail} + 2));
        set(hotspot['text-${id}'].bg, false);
        set(hotspot['text-${id}'].width, 'auto');
        set(hotspot['text-${id}'].height, 'auto');
        set(hotspot['text-${id}'].align, 'center');
        set(hotspot['text-${id}'].valign, 'middle');
        set(hotspot['text-${id}'].ox, -20);
        set(hotspot['text-${id}'].oy, -20);
        set(hotspot['text-${id}'].scale, 1);
        set(hotspot['text-${id}'].zoom, true);
        set(hotspot['text-${id}'].css, "${textCss}");
        set(hotspot['text-${id}'].html, "${safeText}");
        set(hotspot['text-${id}'].zorder, 36);
        set(hotspot['text-${id}'].enabled, false);
        set(hotspot['text-${id}'].capture, false);
        set(hotspot['text-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['text-${id}'].anno_kind, 'annotation');
        set(hotspot['text-${id}'].object_cat, '${cat}');
      `)
    } else {
      krpano.call(`removehotspot('text-${id}')`)
    }

    const yearStr = year ? String(year).trim() : ''
    if (yearStr) {
      const safeYearHtml =
        `<div class="rn-badge-year" style="color: ${catColor};">${yearStr}</div>`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      krpano.call(`
        addhotspot('year-${id}');
        set(hotspot['year-${id}'].type, 'text');
        set(hotspot['year-${id}'].ath, ${hTail});
        set(hotspot['year-${id}'].atv, ${vTail});
        set(hotspot['year-${id}'].edge, 'center');
        set(hotspot['year-${id}'].bg, false);
        set(hotspot['year-${id}'].css, '');
        set(hotspot['year-${id}'].padding, 0);
        set(hotspot['year-${id}'].anno_kind, 'annotation');
        set(hotspot['year-${id}'].object_cat, '${cat}');
        set(hotspot['year-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['year-${id}'].zorder, 35);
        set(hotspot['year-${id}'].oy, 21);
        set(hotspot['year-${id}'].html, "${safeYearHtml}");
      `)
    } else {
      krpano.call(`removehotspot('year-${id}')`)
    }
  }

  /**
   * Renders Renewal Comment Hotspot into Krpano viewer
   */
  static AddRNCommentHotspot = (options: AddRNCommentOptions) => {
    const { krpano, id, h, v, annotation, isDisplay, userList } = options
    KrpanoRN.EnsureRNMarkerStyles()

    krpano.call(`
      remove_annotation(${id});
      removehotspot(id-${id});
      removehotspot(year-${id});
      removehotspot(empty-${id});
      removehotspot(tooltip-${id});
      removehotspot(text-${id});
    `)

    const info = CommentDomainService.extractCommentMarkerInfo(
      annotation,
      userList
    )
    const {
      isResolved,
      objectCat,
      isLocked,
      titleText,
      recipientCount,
      isAllRecipients,
      year
    } = info
    const catColor = isResolved
      ? RESOLVED_COMMENT_COLOR
      : KrpanoRN.GetCategoryColor(objectCat)

    const hasBranches =
      annotation.head_h !== undefined &&
      annotation.head_h !== null &&
      annotation.mid_h !== undefined &&
      annotation.mid_h !== null

    const hHead = hasBranches ? annotation.head_h : h ?? 0
    const vHead = hasBranches ? annotation.head_v : v ?? 0
    const hMid = hasBranches ? annotation.mid_h : h ?? 0
    const vMid = hasBranches ? annotation.mid_v : v ?? 0
    const hTail = h ?? annotation.h ?? 0
    const vTail = v ?? annotation.v ?? 0

    let lockBadgeHtml = ''
    if (isLocked) {
      lockBadgeHtml = `<div class="rn-badge rn-badge-lock"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg></div>`
    }

    const displayTitle = isResolved ? `${titleText} (解決済)` : titleText
    const safeTitle = displayTitle
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
    const tooltipHtml = `<div class="rn-marker-tooltip">${safeTitle}</div>`

    const stemDotHtml = hasBranches
      ? ''
      : `
      <svg class="rn-marker-stem" viewBox="0 0 2 24">
        <line x1="1" y1="0" x2="1" y2="24" stroke="#ffffff" stroke-width="1.2" stroke-opacity="0.85"></line>
      </svg>
      <span class="rn-marker-dot"></span>
    `

    let recipientBadgeHtml = ''
    const badgeText = isAllRecipients ? '全' : recipientCount
    const badgeBgColor = isAllRecipients
      ? 'rgb(22, 163, 74)'
      : 'rgb(14, 165, 233)'
    recipientBadgeHtml = `
      <span class="absolute -right-1.5 -top-1.5" style="position: absolute; top: -6px; right: -6px;">
        <span class="flex items-center gap-0.5 rounded-full px-1.5 ring-2 ring-white" style="background-color: ${badgeBgColor}; height: 16px; padding-top: 1px; padding-bottom: 1px; display: inline-flex; align-items: center; justify-content: center; border-radius: 9999px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <span class="text-[9px] font-bold text-white leading-none" style="font-size: 9px; font-weight: 700; color: white; margin-left: 2px;">${badgeText}</span>
        </span>
      </span>
    `

    const markerSize = isResolved ? 20 : 40

    let markerInnerHtml = ''
    if (isResolved) {
      markerInnerHtml = `
        <div class="kr-anno-inner" style="position: relative; display: inline-flex; flex-direction: column; align-items: center; width: ${markerSize}px; height: ${markerSize}px;">
          <div class="rn-marker-container" style="width: ${markerSize}px; height: ${markerSize}px;">
            <div class="rn-comment-marker-resolved" style="background-color: ${catColor};">
              <span style="color: white; font-size: 11px; font-weight: 900;">✓</span>
            </div>
            ${lockBadgeHtml}
            ${tooltipHtml}
          </div>
          ${stemDotHtml}
        </div>
      `
    } else {
      markerInnerHtml = `
        <div class="kr-anno-inner" style="position: relative; display: inline-flex; flex-direction: column; align-items: center; width: ${markerSize}px; height: ${markerSize}px;">
          <div class="rn-marker-container" style="width: ${markerSize}px; height: ${markerSize}px;">
            <div class="rn-comment-marker-open" style="background-color: ${catColor};">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                <path d="M8 10h.01"></path>
                <path d="M12 10h.01"></path>
                <path d="M16 10h.01"></path>
              </svg>
            </div>
            <div class="rn-badge rn-badge-status-open">!</div>
            ${recipientBadgeHtml}
            ${lockBadgeHtml}
            ${tooltipHtml}
          </div>
          ${stemDotHtml}
        </div>
      `
    }

    const safeMarkerHtml = markerInnerHtml
      .replace(/"/g, "'")
      .replace(/,/g, '&#44;')

    let branchHotspotsXml = ''
    let tailDragMode = 'new_tour_tail'

    if (hasBranches) {
      tailDragMode = 'tail'
      const hexColor = KrpanoRN.toKrpanoHexColor(catColor)

      const headHtml =
        `<div style="width:16px;height:16px;border-radius:50%;background-color:${catColor};border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.35);cursor:pointer;box-sizing:border-box;"></div>`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      const middleHtml =
        `<div style="width:10px;height:10px;border-radius:50%;background-color:${catColor};border:1.5px solid #ffffff;box-shadow:0 1px 2px rgba(0,0,0,0.35);cursor:pointer;box-sizing:border-box;"></div>`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      branchHotspotsXml = `
        addhotspot('head-${id}');
        set(hotspot['head-${id}'].type, 'text');
        set(hotspot['head-${id}'].ath, ${hHead});
        set(hotspot['head-${id}'].atv, ${vHead});
        set(hotspot['head-${id}'].edge, 'center');
        set(hotspot['head-${id}'].bg, false);
        set(hotspot['head-${id}'].css, '');
        set(hotspot['head-${id}'].padding, 0);
        set(hotspot['head-${id}'].bgborder, '0');
        set(hotspot['head-${id}'].html, "${headHtml}");
        set(hotspot['head-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['head-${id}'].zorder, 32);
        set(hotspot['head-${id}'].anno_kind, 'comment');
        set(hotspot['head-${id}'].object_cat, '${objectCat || ''}');
        set(hotspot['head-${id}'].ondown, drag_hotspot('${id}', 'head'););
        set(hotspot['head-${id}'].onup, if(islocked, drop_hotspot();js(handle_drag_end('${id}', 'head'));););

        addhotspot('middle-${id}');
        set(hotspot['middle-${id}'].type, 'text');
        set(hotspot['middle-${id}'].ath, ${hMid});
        set(hotspot['middle-${id}'].atv, ${vMid});
        set(hotspot['middle-${id}'].edge, 'center');
        set(hotspot['middle-${id}'].bg, false);
        set(hotspot['middle-${id}'].css, '');
        set(hotspot['middle-${id}'].padding, 0);
        set(hotspot['middle-${id}'].bgborder, '0');
        set(hotspot['middle-${id}'].html, "${middleHtml}");
        set(hotspot['middle-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['middle-${id}'].zorder, 32);
        set(hotspot['middle-${id}'].anno_kind, 'comment');
        set(hotspot['middle-${id}'].object_cat, '${objectCat || ''}');
        set(hotspot['middle-${id}'].ondown, drag_hotspot('${id}', 'middle'););
        set(hotspot['middle-${id}'].onup, if(islocked, drop_hotspot();js(handle_drag_end('${id}', 'middle'));););

        addhotspot('polyline-${id}');
        hotspot['polyline-${id}'].loadstyle('polyline_style_memo');
        set(hotspot['polyline-${id}'].polyline, true);
        set(hotspot['polyline-${id}'].point[0].ath, ${hHead});
        set(hotspot['polyline-${id}'].point[0].atv, ${vHead});
        set(hotspot['polyline-${id}'].point[1].ath, ${hMid});
        set(hotspot['polyline-${id}'].point[1].atv, ${vMid});
        set(hotspot['polyline-${id}'].bordercolor, ${hexColor});
        set(hotspot['polyline-${id}'].borderwidth, 2);
        set(hotspot['polyline-${id}'].borderalpha, 1);
        set(hotspot['polyline-${id}'].scale, 1);
        set(hotspot['polyline-${id}'].zoom, true);
        set(hotspot['polyline-${id}'].capture, false);
        set(hotspot['polyline-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['polyline-${id}'].zorder, 28);
        set(hotspot['polyline-${id}'].anno_kind, 'comment');
        set(hotspot['polyline-${id}'].object_cat, '${objectCat || ''}');

        addhotspot('polyline2-${id}');
        hotspot['polyline2-${id}'].loadstyle('polyline_style_memo');
        set(hotspot['polyline2-${id}'].polyline, true);
        set(hotspot['polyline2-${id}'].point[0].ath, ${hMid});
        set(hotspot['polyline2-${id}'].point[0].atv, ${vMid});
        set(hotspot['polyline2-${id}'].point[1].ath, ${hTail});
        set(hotspot['polyline2-${id}'].point[1].atv, ${vTail});
        set(hotspot['polyline2-${id}'].bordercolor, ${hexColor});
        set(hotspot['polyline2-${id}'].borderwidth, 2);
        set(hotspot['polyline2-${id}'].borderalpha, 1);
        set(hotspot['polyline2-${id}'].scale, 1);
        set(hotspot['polyline2-${id}'].zoom, true);
        set(hotspot['polyline2-${id}'].capture, false);
        set(hotspot['polyline2-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['polyline2-${id}'].zorder, 28);
        set(hotspot['polyline2-${id}'].anno_kind, 'comment');
        set(hotspot['polyline2-${id}'].object_cat, '${objectCat || ''}');
      `
    }

    krpano.call(`
      ${branchHotspotsXml}
      addhotspot('id-${id}');
      set(hotspot['id-${id}'].type, 'text');
      set(hotspot['id-${id}'].ath, ${hTail});
      set(hotspot['id-${id}'].atv, ${vTail});
      set(hotspot['id-${id}'].width, ${markerSize});
      set(hotspot['id-${id}'].height, ${markerSize});
      set(hotspot['id-${id}'].edge, 'center');
      set(hotspot['id-${id}'].align, 'center');
      set(hotspot['id-${id}'].bg, false);
      set(hotspot['id-${id}'].css, '');
      set(hotspot['id-${id}'].padding, 0);
      set(hotspot['id-${id}'].bgborder, '0');
      set(hotspot['id-${id}'].anno_kind, 'comment');
      set(hotspot['id-${id}'].object_cat, '${objectCat || ''}');
      set(hotspot['id-${id}'].visible, ${isDisplay || 'true'});
      set(hotspot['id-${id}'].zorder, 34);
      set(hotspot['id-${id}'].html, "${safeMarkerHtml}");
      set(hotspot['id-${id}'].onclick, js(handle_click_annotation('${id}')););
      set(hotspot['id-${id}'].ondown, drag_hotspot('${id}', '${tailDragMode}'););
      set(hotspot['id-${id}'].onup, if(islocked, drop_hotspot();js(handle_drag_end('${id}'));););
    `)

    krpano.call(`removehotspot(year-${id})`)
    const yearStr = year ? String(year).trim() : ''
    if (yearStr) {
      const safeYearHtml =
        `<div class="rn-badge-year" style="color: ${catColor};">${yearStr}</div>`
          .replace(/"/g, "'")
          .replace(/,/g, '&#44;')

      krpano.call(`
        addhotspot('year-${id}');
        set(hotspot['year-${id}'].type, 'text');
        set(hotspot['year-${id}'].ath, ${hTail});
        set(hotspot['year-${id}'].atv, ${vTail});
        set(hotspot['year-${id}'].edge, 'center');
        set(hotspot['year-${id}'].bg, false);
        set(hotspot['year-${id}'].css, '');
        set(hotspot['year-${id}'].padding, 0);
        set(hotspot['year-${id}'].anno_kind, 'comment');
        set(hotspot['year-${id}'].object_cat, '${objectCat || ''}');
        set(hotspot['year-${id}'].visible, ${isDisplay || 'true'});
        set(hotspot['year-${id}'].zorder, 35);
        set(hotspot['year-${id}'].oy, 24);
        set(hotspot['year-${id}'].html, "${safeYearHtml}");
      `)
    }
  }

  /**
   * Dynamically filters Krpano markers based on active categories and features
   */
  static ApplyMarkerFilter(
    selectedCategories: string[] = KrpanoRN.categories,
    selectedFeatures: string[] = KrpanoRN.features
  ): void {
    KrpanoRN.categories = selectedCategories
    KrpanoRN.features = selectedFeatures

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('rn_marker_filter_change', {
          detail: {
            categories: selectedCategories,
            features: selectedFeatures
          }
        })
      )
    }

    const krpano =
      new KrpanoEngine('full').instance || new KrpanoEngine('viewer').instance
    if (!krpano || typeof krpano.get !== 'function') return

    const count = Number(krpano.get('hotspot.count')) || 0
    for (let i = 0; i < count; i += 1) {
      const name = krpano.get(`hotspot[${i}].name`)
      const isSystemHotspot =
        !name ||
        typeof name !== 'string' ||
        name.startsWith('vtourskin') ||
        name.startsWith('webvr') ||
        name === 'tripod' ||
        name.startsWith('arrow') ||
        name.startsWith('stair') ||
        name.startsWith('temp_')

      // Memo is identical to other series and not subject to RN category/feature filtering
      const isMemoHotspot =
        name.includes('memo') ||
        krpano.get(`hotspot[${name}].anno_kind`) === 'memo'

      if (!isSystemHotspot && !isMemoHotspot) {
        let annoKind = krpano.get(`hotspot[${name}].anno_kind`) as
          | string
          | undefined
        if (!annoKind) {
          annoKind = name.includes('comment') ? 'comment' : 'annotation'
        }

        let rawCat = (krpano.get(`hotspot[${name}].object_cat`) || '') as string

        if (!rawCat) {
          const match = name.match(
            /^(text|tooltip|head|middle|polyline|polyline2|empty|year)-(.+)$/
          )
          if (match) {
            const targetId = match[2]
            rawCat = (krpano.get(`hotspot[id-${targetId}].object_cat`) ||
              krpano.get(`hotspot[head-${targetId}].object_cat`) ||
              '') as string
            const mainKind =
              krpano.get(`hotspot[id-${targetId}].anno_kind`) ||
              krpano.get(`hotspot[head-${targetId}].anno_kind`)
            if (mainKind) {
              annoKind = mainKind as string
            }
          }
        }

        const annoCat = String(rawCat).trim().toUpperCase()

        const matchFeature = selectedFeatures.includes(annoKind)
        let matchCategory = true
        if (annoCat) {
          matchCategory = selectedCategories.some(
            (c) => c.trim().toUpperCase() === annoCat
          )
        }

        const shouldShow = matchFeature && matchCategory
        krpano.set(`hotspot[${name}].visible`, shouldShow)
      }
    }
  }
}

export default KrpanoRN
